import { CUSTOMER_POSITIONS_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartiesFilter } from '@src/hooks';
import { useCustomers } from '@src/hooks/useCustomer';
import { CUSTOMER_MARGIN, EntityAdmin, useMarketAccountStatusesFilter } from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-enterprise';
import { isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useExpireOptionDialog } from '../useExpireOptionDialog';
import type { CustomerPosition } from './types';
import { CUSTOMER_POSITIONS_COLUMNS, getCustomerPositionsDrawerOptions } from './utils';

const WS_REQUEST = { name: CUSTOMER_MARGIN, tag: 'CustomerPositions' };

export function CustomerPositions() {
  const customers = useCustomers();
  const { getExpireOptionMenuItem, expireOptionDialog } = useExpireOptionDialog();

  const drawerOptions = useMemo(() => {
    if (!customers) {
      return undefined;
    }
    return getCustomerPositionsDrawerOptions({ customers });
  }, [customers]);

  const statusFilter = useMarketAccountStatusesFilter({ label: 'Status' });
  const counterpartiesFilter = useCounterpartiesFilter({ label: 'Customer', nullUntilLoaded: true });
  const availableFilterableProperties = useMemo(() => {
    const properties = [counterpartiesFilter, statusFilter];
    // As per the Entity Admin API, undefined is used to indicate a loading state
    return properties.every(prop => !isNil(prop)) ? properties : undefined;
  }, [counterpartiesFilter, statusFilter]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_POSITIONS_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  const getCustomContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => {
      const expireItem = getExpireOptionMenuItem(params);
      return expireItem ? [expireItem] : [];
    },
    [getExpireOptionMenuItem]
  );

  return (
    <>
      <EntityAdmin<CustomerPosition>
        title="Customer Positions"
        subtitle="View customer derivative positions and available margin."
        entityName="Customer Position"
        path="/customer-margins"
        wsRequest={WS_REQUEST}
        childIDField="Symbol"
        entityIDField="MarketAccount"
        columns={CUSTOMER_POSITIONS_COLUMNS}
        allowAddEntity={false}
        allowEditEntity={false}
        allowDeleteEntity={false}
        persistKey="dealer/customer-positions"
        filterableProperties={filterableProperties}
        externallyControlledFilter={externallyControlledFilter}
        filterableServerFields={['Counterparty']}
        drawerOptions={drawerOptions}
        getCustomContextMenuItems={getCustomContextMenuItems}
        groupColumnDef={{
          headerName: 'Customer',
          width: 200,
          valueFormatter: ({ context, value }) => {
            const counterparty = context.current.marketAccountsByName?.get(value)?.Counterparty;
            const customer = counterparty ? context.current.customersByName?.get(counterparty) : undefined;
            return customer?.DisplayName ?? value;
          },
        }}
      />
      {expireOptionDialog}
    </>
  );
}
